
import React, { memo, useState, useEffect } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Divider,
  Avatar,
  useMediaQuery,
  Snackbar,
  Alert,
  Badge,
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme as useMuiTheme } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "../../../core/providers/ThemeProvider";
import { colors } from "../../styles/theme/colors";
import { responsivePadding } from "../../utils/responsive";
import CustomButton from "../ui/Button/Custombutton";
import { Logo } from "../common/Logo/Logo";
import translations from "../../translations/en.json";
import { getLogger } from "../../../shared/utils/logger";
import BellIcon from "../../../assets/darkNotif.svg";
import { useUserStore } from "../../../app/store/useUserStore";
import { isTravellerRole } from "../../utils/roleUtils";
import UserMenu from "../../../features/User/UserMenu";
import RoleToggle from "../../../shared/components/ui/RoleToggle/RoleToggle";
import { useRoleSwitch } from "../../../features/User/hooks/useRoleSwitch";
import RoleSelectionDialog from "../../../shared/components/ui/RoleSelectionDialog/RoleSelectionDialog";
import { useInitialRoleSelection } from "../../../features/User/hooks/useInitialRoleSelection";
import { NotificationDropdown } from "../ui/NotificationDropdown";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../../app/store";
import { loadNotifications } from "../../../app/store/slices/notificationSlice";
import { UserAvatar } from "../ui/Avatar";

export const NavigationBar: React.FC = memo(() => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [bellAnchorEl, setBellAnchorEl] = useState<null | HTMLElement>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const { theme } = useTheme();
  const { isAuthenticated, loginWithRedirect, user: auth0User } = useAuth0();
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down("lg"));
  const dispatch = useDispatch();

  const { user } = useUserStore();
  const { switchRole, isSwitching, error: roleSwitchError, clearError } = useRoleSwitch();

  // Get role-based unread count (excluding other users' notifications)
  const allNotifications = useSelector((state: RootState) => state.notifications.notifications);
  const currentRole = user?.user_role;
  const currentUserId = user?.uuid || String(user?.user_id || '');
  // Fallback for notifications cached before uuid was populated
  const legacyUserId = user?.user_id !== undefined ? String(user.user_id) : '';
  const unreadCount = (allNotifications || []).filter(n => {
    if (n.isRead) return false;
    if (n.type === 'role_switch') return false;
    if (currentUserId && n.userId && n.userId !== currentUserId && !(legacyUserId && n.userId === legacyUserId)) return false;
    if (currentRole && n.user_role && n.user_role !== currentRole) return false;
    return true;
  }).length;
  const {
    showDialog: showRoleSelectionDialog,
    setInitialRole,
    isSettingRole,
    error: roleSelectionError,
    clearError: clearRoleSelectionError,
  } = useInitialRoleSelection();

  // Note: User profile is fetched globally by AuthInitializer
  // We just read from the store here - no need to fetch again

  // Load notifications on mount
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(loadNotifications());
    }
  }, [isAuthenticated, dispatch]);

  // Handle role switch with optimistic UI update
  const handleRoleToggle = async () => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: location.pathname },
      });
      return;
    }

    const success = await switchRole();
    
    if (success) {
      setShowSuccessMessage(true);
      setIsMobileMenuOpen(false);
      
      // Navigate to home after successful role switch
      // This ensures the UI reflects the new role
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 500);
    }
  };

  // Clear error when mobile menu closes
  useEffect(() => {
    if (!isMobileMenuOpen && roleSwitchError) {
      clearError();
    }
  }, [isMobileMenuOpen, roleSwitchError, clearError]);

  const handleNavigation = (path: string) => {
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      navigate(path);
    }, 0);
  };

  const handleSignup = () => {
    loginWithRedirect({
      screen_hint: "signup",
      appState: { returnTo: window.location.origin },
    }).catch((err) => {
    });
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBellClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setBellAnchorEl(bellAnchorEl ? null : event.currentTarget);
  };

  const handleNotificationClose = () => {
    setBellAnchorEl(null);
  };

  const getInitials = (name?: string): string => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const textColor = "#000000";
  const logoVariant = "dark";
  const profileImage = user?.profile_image;

  // Show the role toggle whenever the user has a role set
  const shouldShowRoleToggle = isAuthenticated && !!user?.user_role;

  // Handle initial role selection
  const handleInitialRoleSelect = async (role: 'traveller' | 'sender') => {
    try {
      await setInitialRole(role);
      // After role selection, send new users to complete their profile
      setTimeout(() => {
        navigate('/update-profile', { replace: true });
      }, 300);
    } catch { /* ignore */ }
  };

  return (
    <>
      {/* Navigation Bar with 3D effect */}
      <Box
        component="nav"
        sx={{
          backgroundColor: colors.neutral.white,
          borderRadius: {
            xs: "12px",
            sm: "14px",
            md: "14px",
            lg: "16px",
          },
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: {
            xs: "0.75rem 1rem",
            sm: "0.75rem 1rem",
            md: "0.75rem 1rem",
            lg: "0.75rem 1.5rem",
            xl: "0.75rem 2rem",
          },
          height: "67px",
          width: "100%",
          maxWidth: {
            xs: "100%",
            sm: "100%",
            md: "100%",
            lg: "1360px",
            xl: "1440px",
            xxl: "1536px",
          },
          boxSizing: "border-box",
          overflow: "hidden",
          '@media (max-width: 310px) and (max-height: 300px)': {
            height: "48px",
            padding: "0.5rem",
          },
        }}
      >
        {/* Layout: Logo Left, Actions Right */}
        {isAuthenticated ? (
          <>
            {/* Left side - Logo */}
            <Box sx={{ cursor: "pointer" }} onClick={() => handleNavigation("/")}>
              <Logo
                variant={logoVariant}
                sx={{
                  width: { xs: "100px", sm: "110px", lg: "124px" },
                  height: { xs: "29px", sm: "32px", lg: "36px" },
                }}
              />
            </Box>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Right side - Actions */}
            {!isSmallScreen ? (
              <Stack
                direction="row"
                spacing={{ lg: 1, xl: 1.5 }}
                alignItems="center"
                sx={{ flex: 1, justifyContent: "flex-end" }}
              >
                {/* Role Toggle - Only show if profile is complete */}
                {shouldShowRoleToggle && (
                  <RoleToggle
                    currentRole={user?.user_role || ""}
                    onChange={handleRoleToggle}
                    disabled={!isAuthenticated}
                    loading={isSwitching}
                  />
                )}

                {/* Notifications */}
                <IconButton onClick={handleBellClick} aria-label={translations.navigation.notifications}>
                  <Badge
                    badgeContent={Math.min(unreadCount, 30)}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: isTravellerRole(user?.user_role) ? '#9B1B5A' : '#7A0056',
                        color: 'white',
                        fontSize: '0.65rem',
                        height: '18px',
                        minWidth: '18px',
                        borderRadius: '9px',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={BellIcon}
                      alt={translations.navigation.notifications}
                      sx={{
                        width: 24,
                        height: 24,
                        filter: unreadCount > 0
                          ? `drop-shadow(0 0 2px ${isTravellerRole(user?.user_role) ? '#9B1B5A' : '#7A0056'})`
                          : 'none',
                      }}
                    />
                  </Badge>
                </IconButton>

                {/* Avatar */}
                <IconButton
                  onClick={handleAvatarClick}
                  sx={{ p: 0 }}
                  aria-label="Profile"
                >
                  <UserAvatar
                    src={profileImage}
                    name={user?.full_name || user?.name}
                    email={user?.email || auth0User?.email}
                    userRole={user?.user_role}
                    size={32}
                    fontSize="14px"
                  />
                </IconButton>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, justifyContent: "flex-end" }}>
                {/* Notifications Bell Icon */}
                <IconButton onClick={handleBellClick} aria-label={translations.navigation.notifications}>
                  <Badge
                    badgeContent={Math.min(unreadCount, 30)}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: isTravellerRole(user?.user_role) ? '#9B1B5A' : '#7A0056',
                        color: 'white',
                        fontSize: '0.65rem',
                        height: '18px',
                        minWidth: '18px',
                        borderRadius: '9px',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={BellIcon}
                      alt={translations.navigation.notifications}
                      sx={{
                        width: 24,
                        height: 24,
                        filter: unreadCount > 0
                          ? `drop-shadow(0 0 2px ${isTravellerRole(user?.user_role) ? '#9B1B5A' : '#7A0056'})`
                          : 'none',
                      }}
                    />
                  </Badge>
                </IconButton>

                {/* Hamburger Menu */}
                <IconButton onClick={toggleMobileMenu}>
                  {isMobileMenuOpen ? (
                    <CloseIcon sx={{ width: 24, height: 24, color: textColor }} />
                  ) : (
                    <MenuIcon sx={{ width: 24, height: 24, color: textColor }} />
                  )}
                </IconButton>
              </Stack>
            )}
          </>
        ) : (
          <>
            {/* Unauthenticated Layout: Logo Left, Signup Right */}
            {/* Left side - Logo */}
            <Box sx={{ cursor: "pointer" }} onClick={() => handleNavigation("/")}>
              <Logo
                variant={logoVariant}
                sx={{
                  width: { xs: "100px", sm: "110px", lg: "124px" },
                  height: { xs: "29px", sm: "32px", lg: "36px" },
                }}
              />
            </Box>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Right side — Actions */}
            {!isSmallScreen ? (
              <Stack
                direction="row"
                spacing={{ lg: 1, xl: 1.5 }}
                alignItems="center"
                sx={{ flex: 1, justifyContent: "flex-end" }}
              >
                {/* Role Toggle - Only show if profile is complete */}
                {shouldShowRoleToggle && (
                  <RoleToggle
                    currentRole={user?.user_role || ""}
                    onChange={handleRoleToggle}
                    disabled={!isAuthenticated}
                    loading={isSwitching}
                  />
                )}

                <CustomButton
                  onClick={handleSignup}
                  buttonText={translations.navigation.signup}
                  sx={{
                    backgroundColor: colors.button.primary,
                    color: colors.neutral.white,
                    fontSize: { lg: "0.85rem", xl: "0.9rem" },
                    fontWeight: 500,
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: { lg: "8px 16px", xl: "8px 20px" },
                    "&:hover": {
                      backgroundColor: colors.button.primaryHover,
                    },
                  }}
                />
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, justifyContent: "flex-end" }}>
                <IconButton onClick={toggleMobileMenu}>
                  {isMobileMenuOpen ? (
                    <CloseIcon sx={{ width: 24, height: 24, color: textColor }} />
                  ) : (
                    <MenuIcon sx={{ width: 24, height: 24, color: textColor }} />
                  )}
                </IconButton>
              </Stack>
            )}
          </>
        )}
        </Box>

      {/* User Menu — renders as a dropdown on desktop/tablet and a top Drawer on mobile,
          triggered by either the avatar click (anchorEl) or the hamburger (isMobileMenuOpen). */}
      {isAuthenticated && (
        <UserMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) || (isMobileMenuOpen && isSmallScreen)}
          onClose={() => {
            handleMenuClose();
            setIsMobileMenuOpen(false);
          }}
          mobileHeaderSlot={
            shouldShowRoleToggle ? (
              <RoleToggle
                currentRole={user?.user_role || ""}
                onChange={handleRoleToggle}
                disabled={!isAuthenticated}
                loading={isSwitching}
              />
            ) : undefined
          }
        />
      )}

      {/* Signup prompt — mobile hamburger menu when signed out */}
      {!isAuthenticated && (
        <Drawer
          anchor="top"
          open={isMobileMenuOpen && isSmallScreen}
          onClose={toggleMobileMenu}
          sx={{
            "& .MuiDrawer-paper": {
              mt: {
                xs: "calc(67px + 1.5rem)",
                sm: "calc(67px + 1.75rem)",
                md: "calc(67px + 1.75rem)",
                lg: "calc(67px + 2.25rem)",
                xl: "calc(67px + 2.25rem)",
              },
              mx: {
                xs: "0.5rem",
                sm: "0.75rem",
                md: "0.75rem",
                lg: "1.5rem",
                xl: "1.5rem",
              },
              backgroundColor: colors.neutral.white,
              borderRadius: "0 0 16px 16px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)",
              padding: responsivePadding.container.mobile,
            },
          }}
        >
          <Stack spacing={1}>
            <Divider sx={{ borderColor: "rgba(0,0,0,0.1)", my: 0.5 }} />

            <CustomButton
              onClick={handleSignup}
              buttonText={translations.navigation.signup}
              fullWidth
              sx={{
                backgroundColor: colors.button.primary,
                color: colors.neutral.white,
                "&:hover": {
                  backgroundColor: colors.button.primaryHover,
                },
              }}
            />
          </Stack>
        </Drawer>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Role switched successfully!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!roleSwitchError}
        autoHideDuration={5000}
        onClose={clearError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={clearError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {roleSwitchError}
        </Alert>
      </Snackbar>

      {/* Role Selection Dialog - Shows when user has completed profile but hasn't selected a role */}
      <RoleSelectionDialog
        open={showRoleSelectionDialog}
        onClose={() => {
          // Don't allow closing until role is selected
        }}
        onRoleSelect={handleInitialRoleSelect}
        loading={isSettingRole}
      />

      {/* Role Selection Error Snackbar */}
      <Snackbar
        open={!!roleSelectionError}
        autoHideDuration={5000}
        onClose={clearRoleSelectionError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={clearRoleSelectionError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {roleSelectionError}
        </Alert>
      </Snackbar>

      {/* Notification Dropdown */}
      <NotificationDropdown
        isOpen={Boolean(bellAnchorEl)}
        onClose={handleNotificationClose}
        anchorEl={bellAnchorEl}
      />
    </>
  );
});

NavigationBar.displayName = "NavigationBar";